import { useEffect, useRef, useState } from "react";
import { useAuth } from "@clerk/react";
import { toast } from "sonner";
import { Send, Sparkles, Trash2, Loader2 } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { useAdminApi, selectCls, btnPrimary, btnGhost, cardCls } from "./useAdminApi";
import { API } from "@/lib/api";

const MODELS = [["claude", "Claude Sonnet 5.5"], ["gpt", "ChatGPT · GPT-5.6 Terra"], ["gemini", "Gemini 3.1 Pro"]];
const PROMPTS = [
  "Summarise today's and tomorrow's appointments with any notes I should know",
  "Draft a friendly WhatsApp reminder for tomorrow's patients",
  "Draft a warm reply to a 5-star Google review about a painless root canal",
];

export const AiPanel = () => {
  const call = useAdminApi();
  const { getToken } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [model, setModel] = useState("claude");
  const [busy, setBusy] = useState(false);
  const end = useRef(null);
  useEffect(() => { call("get", "/admin/ai/history").then((h) => setMsgs((m) => (m.length ? m : h))); }, [call]);
  useEffect(() => { end.current?.scrollIntoView({ block: "nearest" }); }, [msgs]);

  const send = async (message) => {
    if (!message.trim() || busy) return;
    setBusy(true); setText("");
    setMsgs((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/admin/ai/chat`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${await getToken()}` }, body: JSON.stringify({ message, model }) });
      if (!res.ok) throw new Error("failed");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n"); buf = parts.pop();
        for (const p of parts) {
          const ev = JSON.parse(p.replace(/^data: /, ""));
          if (ev.error) toast.error(ev.error);
          if (ev.delta) setMsgs((m) => {
            const last = m[m.length - 1];
            return last?.role === "assistant" ? [...m.slice(0, -1), { ...last, content: last.content + ev.delta }] : [...m, { role: "assistant", content: ev.delta }];
          });
        }
      }
    } catch { toast.error("The AI assistant is unavailable right now."); } finally { setBusy(false); }
  };
  const clear = async () => { await call("delete", "/admin/ai/history"); setMsgs([]); };

  return (
    <div className={`${cardCls} mt-4`} data-testid="ai-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-xl font-semibold flex items-center gap-2"><Sparkles className="h-5 w-5 text-teal" />Clinic AI assistant</h3>
        <div className="flex gap-2">
          <select value={model} onChange={(e) => setModel(e.target.value)} className={selectCls} data-testid="ai-model-select">
            {MODELS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <button className={btnGhost} onClick={clear} data-testid="ai-clear-button"><Trash2 className="h-3.5 w-3.5" />Clear</button>
        </div>
      </div>
      <p className="text-xs text-ink-muted mt-1">For staff only. Drafts messages and summaries — always review before sending to a patient.</p>
      <div className="mt-4 h-[380px] overflow-y-auto rounded-2xl bg-alabaster border border-slate-100 p-4 space-y-3" data-testid="ai-messages">
        {msgs.length === 0 && (
          <div className="space-y-2">{PROMPTS.map((p, i) => <button key={p} onClick={() => send(p)} className="block w-full text-left text-sm rounded-xl border border-mint-border bg-white px-3 py-2 hover:bg-mint" data-testid={`ai-prompt-${i}`}>{p}</button>)}</div>
        )}
        {msgs.map((m, i) => (
          <div key={i} className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${m.role === "user" ? "ml-auto bg-teal text-white" : "bg-white border border-slate-200"}`} data-testid={`ai-message-${m.role}`}>
            {m.content || <Loader2 className="h-4 w-4 animate-spin" />}
          </div>
        ))}
        <div ref={end} />
      </div>
      <form onSubmit={(e) => { e.preventDefault(); send(text); }} className="flex gap-2 mt-3">
        <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Ask anything, e.g. draft a post-extraction care message" className="rounded-xl"
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(text); } }} data-testid="ai-input" />
        <button type="submit" disabled={busy || !text.trim()} className={`${btnPrimary} h-auto`} data-testid="ai-send-button"><Send className="h-4 w-4" /></button>
      </form>
    </div>
  );
};
